import { Injectable, OnDestroy } from '@angular/core';
import { CollabFileType } from '@common/collab/enums/collab-file-type.enum';
import { CollabService } from '@portal-core/collab/services/collab.service';
import { StringService } from '@portal-core/general/services/string.service';
import { ProjectFileType } from '@portal-core/project-files/enums/project-file-type.enum';
import { ProjectFilesService } from '@portal-core/project-files/services/project-files.service';
import { WorkspaceSessionService } from '@portal-core/workspaces/services/workspace-session.service';
import { Base64 } from 'js-base64';
import { catchError, filter, map, merge, Observable, of, shareReplay, Subject, takeUntil, tap, throwError } from 'rxjs';

/**
 * App-wide cache for the slow per-file collab/source-control fetches that the LWE editor needs to
 * compute styles. A single root-scoped instance is shared by the workspace page, the reviews page,
 * and any MatDialog editors they open — guaranteeing the dialog and its host always see the same
 * cache and the same invalidation events.
 *
 * Why this exists: every `load()` of a topic re-fetches the project's `.flprj` plus each
 * project/branding stylesheet from the collab server, which has high per-request overhead. None of
 * these change within a single editing session in a typical workflow, so caching them eliminates
 * the dominant cost of the second-and-subsequent topic open.
 *
 * Why root-scoped: per-page scoping cleanly handled the "another user edited stylesheets while you
 * were away" case but split the cache so dialogs opened outside the page's subtree (MatDialog
 * attaches to the root injector by default) got a separate empty instance. Root-scoping unifies
 * everyone; entry-point components compensate for the staleness case by calling `invalidateAll()`
 * in their constructor — see `ProjectWorkspacePageComponent` and `ReviewsPageComponent`.
 *
 * Live invalidation: subscribes to `WorkspaceSessionService` file events directly and invalidates
 * affected entries. `WorkspaceSessionService` is itself root-scoped and only emits while a workspace
 * session is active, so the subscription is harmless on the reviews path (no events fire there).
 */
@Injectable({ providedIn: 'root' })
export class ProjectStylesheetCacheService implements OnDestroy {
  // key = `${projectId}|${commitIdOrBranchName}|${fileType}`
  private projectFileCache = new Map<string, Observable<HTMLElement>>();
  // key = `${projectId}|${commitIdOrBranchName}|${fileType}|${filePath}`
  private fileContentCache = new Map<string, Observable<string>>();

  private readonly destroyed$ = new Subject<void>();

  constructor(
    private collabService: CollabService,
    private projectFileService: ProjectFilesService,
    private stringService: StringService,
    workspaceSessionService: WorkspaceSessionService,
  ) {
    // `docMerged$` only contributes when the merge won't be followed by a `fileUpdated$` for the
    // same path — i.e. when the post-merge doc has no uncommitted changes. For merges of dirty
    // docs the syncer guarantees a `workspace.file.updated` later, so we let `fileUpdated$`
    // drive invalidation and skip the merge event to avoid invalidating twice.
    const docMergedEvents$ = workspaceSessionService.docMerged$.pipe(
      filter(event => !event.hasUncommittedChanges)
    );
    const fileChangeEvents$ = merge(
      workspaceSessionService.fileDeleted$,
      workspaceSessionService.fileInserted$,
      workspaceSessionService.fileUpdated$,
      docMergedEvents$
    );

    // Invalidate synchronously per event — no buffering — so the cache is always fresh by the time
    // any downstream consumer (e.g. TextEditorCssStylesService's debounced reloadStyles) reads it.
    fileChangeEvents$.pipe(
      takeUntil(this.destroyed$),
    ).subscribe((event) => {
      this.invalidateFileContent(event.filePath);
      // The flare project file (.flprj) drives which style sources are used (BrandingStylesheet, MasterStylesheet, MasterStylesheetOverride),
      // so changes to it must also invalidate the project-file cache.
      if (this.projectFileService.getFileType(event.filePath) === ProjectFileType.Project) {
        this.invalidateProjectFile();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /** Returns the parsed `.flprj` document element, fetched once per (project, branch/commit, fileType) and replayed on subsequent calls. */
  getFlareProjectFile$(projectId: number, commitIdOrBranchName: string, fileType: CollabFileType): Observable<HTMLElement> {
    const key = `${projectId}|${commitIdOrBranchName}|${fileType}`;
    const existing = this.projectFileCache.get(key);
    if (existing) {
      return existing;
    }

    const content$ = fileType === CollabFileType.Review
      ? this.projectFileService.getFlareProjectFileContent$(projectId, commitIdOrBranchName)
      : this.collabService.getFlareProjectFileContent$(projectId, commitIdOrBranchName);

    const cached$ = content$.pipe(
      // Drop the cache entry on error so the next caller retries instead of being served the cached error.
      tap({ error: () => this.projectFileCache.delete(key) }),
      map(content => {
        if (!content) {
          return null;
        }
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(content, 'application/xml');
        return xmlDoc.documentElement;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.projectFileCache.set(key, cached$);
    return cached$;
  }

  /** Returns the text content of the specified file, fetched once per (project, branch/commit, fileType, filePath) and replayed on subsequent calls. */
  loadFileContent$(projectId: number, commitIdOrBranchName: string, fileType: CollabFileType, filePath: string): Observable<string> {
    const key = `${projectId}|${commitIdOrBranchName}|${fileType}|${filePath}`;
    const existing = this.fileContentCache.get(key);
    if (existing) {
      return existing;
    }

    const raw$: Observable<string> = fileType === CollabFileType.Review
      ? this.projectFileService.getProjectFile$(projectId, filePath, commitIdOrBranchName).pipe(
        map(file => file ? this.stringService.stripBOM(Base64.decode(file.Content)) : null)
      )
      : this.collabService.getCollabDocContent$({
        branchName: commitIdOrBranchName,
        filePath: filePath,
        fileType: CollabFileType.Workspace,
        fromRedis: true,
        projectId: projectId,
        reviewFileId: null
      });

    const cached$ = raw$.pipe(
      // catchError must come before the error-tap below: it converts 404s into a normal `null`
      // emission so the tap doesn't fire on them and evict the cached null.
      catchError(error => {
        // 404 means the stylesheet doesn't exist — cache the null so we don't retry on every load.
        if (error.status === 404) {
          return of(null);
        }
        return throwError(error);
      }),
      // Drop the cache entry on any error that survived catchError (i.e. non-404), so the next
      // caller gets a fresh fetch instead of being served the cached error.
      tap({ error: () => this.fileContentCache.delete(key) }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.fileContentCache.set(key, cached$);
    return cached$;
  }

  /** Invalidates the cached file-content entry for the given file path across all keyed projects/branches. Idempotent. */
  invalidateFileContent(filePath: string): void {
    const suffix = `|${filePath}`;
    for (const key of [...this.fileContentCache.keys()]) {
      if (key.endsWith(suffix)) {
        this.fileContentCache.delete(key);
      }
    }
  }

  /** Invalidates all cached `.flprj` entries. Call when the project file itself changes (its contents drive which stylesheets are loaded). */
  invalidateProjectFile(): void {
    this.projectFileCache.clear();
  }

  /** Clears every cached entry. Call on workspace-session entry so stylesheets fetched in a previous session — possibly altered by another user while this user was away — are re-fetched. */
  invalidateAll(): void {
    this.projectFileCache.clear();
    this.fileContentCache.clear();
  }
}
